import { Box, Button, Paper, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { PageHeader } from 'src/ui/shell/PageHeader';
import { enterpriseDataGridSx } from 'src/ui/shell/enterpriseDataGridSx';

type UserRow = {
  id: string;
  tenantId: string;
  username: string;
  kind: 'PUBLIC' | 'PANEL';
  status: 'ACTIVE' | 'DISABLED';
};

const columns: GridColDef<UserRow>[] = [
  { field: 'tenantId', headerName: 'Tenant', width: 160 },
  { field: 'username', headerName: 'Username', flex: 1, minWidth: 240 },
  {
    field: 'kind',
    headerName: 'Kind',
    width: 140,
    renderCell: ({ value }) => (
      <Box
        component="span"
        sx={{
          px: 1.1,
          py: 0.35,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          color: 'primary.light',
          bgcolor: 'rgba(91,156,248,0.1)',
          border: '1px solid rgba(91,156,248,0.28)',
        }}
      >
        {value}
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 140,
    renderCell: ({ value }) => (
      <Box
        component="span"
        sx={{
          px: 1.25,
          py: 0.35,
          borderRadius: 99,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: value === 'ACTIVE' ? 'success.light' : 'error.light',
          bgcolor: value === 'ACTIVE' ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.1)',
          border: `1px solid ${value === 'ACTIVE' ? 'rgba(52,211,153,0.35)' : 'rgba(248,113,113,0.35)'}`,
        }}
      >
        {value}
      </Box>
    ),
  },
];

const rows: UserRow[] = [
  { id: '1', tenantId: 'amn', username: 'superadmin', kind: 'PANEL', status: 'ACTIVE' },
  { id: '2', tenantId: 'amn', username: 'john.doe', kind: 'PUBLIC', status: 'ACTIVE' },
  { id: '3', tenantId: 'demo', username: 'support', kind: 'PANEL', status: 'DISABLED' },
];

export function UsersPage() {
  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="Global directory"
        title="Users"
        description="Cross-tenant principals with lineage back to x-IAM. Filter and export will call your service APIs."
        rightSlot={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button startIcon={<FilterListOutlinedIcon />} variant="outlined" color="inherit" disabled sx={{ fontWeight: 600 }}>
              Saved views
            </Button>
            <Button startIcon={<RefreshOutlinedIcon />} variant="contained" disabled sx={{ fontWeight: 600 }}>
              Refresh
            </Button>
          </Stack>
        }
      />

      <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ height: 540, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 250 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            sx={enterpriseDataGridSx}
          />
        </Box>
      </Paper>
    </Stack>
  );
}
